// TeacherInformationSection.js
import { useState, useEffect } from "react"
import { Chip, Grid, InputAdornment } from "@mui/material"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import DataTable from "components/DataTable"
import moment from "moment"

import Icon from "@mui/material/Icon"
import { Loading } from "shared/components/ui/Loading"
import MDInput from "components/MDInput"
import SearchIcon from "@mui/icons-material/Search"
import ReactPaginate from "react-paginate"
import DateSelect from "components/DateSelect/temp3"
import { useLazyGetLeaveLXOQuery } from "shared/redux/endpoints/lxo"
import { chipStyle } from "components/CenterDashboardSection/style"
import { alertChipStyle } from "./style"
import { useSearchParams } from "react-router-dom"

const TABLE_HEADERS = [
  { Header: "Employee Full Name", accessor: "employeeFullName" },
  { Header: "Leave Type", accessor: "leaveType" },
  { Header: "Status", accessor: "status" },
  { Header: "Start Date", accessor: "startDate" },
  { Header: "End Date", accessor: "endDate" },
  { Header: "Day Type", accessor: "dayType" },
  { Header: "Total Days", accessor: "totalDays" },
  { Header: "Reason", accessor: "reason" },
  { Header: "Attachments", accessor: "attachments" },
  { Header: "Approver Full Name", accessor: "approverFullName" },
  { Header: "Approver Avatar", accessor: "approverAvatar" },
  { Header: "Notification Recipients", accessor: "notificationRecipients" },
  { Header: "Leave Request ID", accessor: "leaveRequestId" },
  { Header: "Employee ID", accessor: "employeeId" },
  { Header: "Approver ID", accessor: "approverId" },
  { Header: "Center ID", accessor: "centerId" },
  { Header: "Rejection Reason", accessor: "rejectionReason" },
  { Header: "Created At", accessor: "createdAt" },
  { Header: "Updated At", accessor: "updatedAt" },
]

export default function TeacherManagementLeaveSection() {
  // State management matching CenterDashboardSection
  const [state, setState] = useState({
    page: 0,
    size: 10,
    keyword: "",
    startDate: moment().startOf("isoweek").format("YYYY-MM-DD"),
    endDate: moment().endOf("isoweek").format("YYYY-MM-DD"),
  })
  const [isLoading, setIsLoading] = useState(true)
  const [dataTable, setDataTable] = useState({
    columns: TABLE_HEADERS,
    rows: [],
  })
  const [pagination, setPagination] = useState({
    totalPage: 0,
    currentPage: 0,
  })
  const [trigger] = useLazyGetLeaveLXOQuery()
  const [start, setStart] = useState(state.start)
  const [end, setEnd] = useState(state.end)
  const [searchParams, setSearchParams] = useSearchParams()

  const handlerDataTable = (data) => {
    const display = (value) => {
      if (value === null || value === undefined || value === "") return "-"
      if (Array.isArray(value)) return JSON.stringify(value)
      if (typeof value === "object") return JSON.stringify(value)
      return value
    }
    const renderAttachments = (attachments) => {
      if (!attachments || attachments.length === 0) return "-"
      if (Array.isArray(attachments)) {
        if (attachments.every((item) => typeof item === "string")) {
          return attachments.join(", ")
        }
        return `${attachments.length} file(s)`
      }
      return display(attachments)
    }
    const renderAvatar = (url, name) => {
      if (!url || url === "-") return "-"
      return (
        <MDBox
          component="img"
          src={url}
          alt={name || "avatar"}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            objectFit: "cover",
            border: "1px solid #e0e0e0",
          }}
        />
      )
    }
    const renderRecipients = (recipients) => {
      if (!recipients || recipients.length === 0) return "-"
      if (!Array.isArray(recipients)) return display(recipients)
      const names = recipients
        .map((r) => {
          if (!r) return null
          if (typeof r === "string") return r
          if (typeof r === "object") {
            return r.fullName || r.name || r.employeeFullName || r.email || null
          }
          return null
        })
        .filter(Boolean)
      if (names.length === 0) return "-"
      return names.slice(0, 2).join(", ")
    }
    setDataTable({
      columns: TABLE_HEADERS,
      rows: data?.data?.map((item) => ({
        leaveRequestId: (
          <MDTypography>{display(item.leaveRequestId)}</MDTypography>
        ),
        employeeId: <MDTypography>{display(item.employeeId)}</MDTypography>,
        leaveType: <MDTypography>{display(item.leaveType)}</MDTypography>,
        startDate: <MDTypography>{display(item.startDate)}</MDTypography>,
        endDate: <MDTypography>{display(item.endDate)}</MDTypography>,
        dayType: <MDTypography>{display(item.dayType)}</MDTypography>,
        totalDays: <MDTypography>{display(item.totalDays)}</MDTypography>,
        reason: <MDTypography>{display(item.reason)}</MDTypography>,
        status: (
          <Chip
            variant="outlined"
            label={display(item.status)}
            sx={(theme) => ({
              ...chipStyle(theme),
              ...alertChipStyle(display(item.status), theme),
            })}
          />
        ),
        approverId: <MDTypography>{display(item.approverId)}</MDTypography>,
        centerId: <MDTypography>{display(item.centerId)}</MDTypography>,
        rejectionReason: (
          <MDTypography>{display(item.rejectionReason)}</MDTypography>
        ),
        createdAt: (
          <MDTypography>
            {item.createdAt
              ? moment(item.createdAt).format("DD/MM/YYYY HH:mm")
              : "-"}
          </MDTypography>
        ),
        updatedAt: (
          <MDTypography>
            {item.updatedAt
              ? moment(item.updatedAt).format("DD/MM/YYYY HH:mm")
              : "-"}
          </MDTypography>
        ),
        attachments: (
          <MDTypography>{renderAttachments(item.attachments)}</MDTypography>
        ),
        employeeFullName: (
          <MDTypography>{display(item.employeeFullName)}</MDTypography>
        ),
        employeeAvatar: renderAvatar(
          item.employeeAvatar,
          item.employeeFullName
        ),
        approverFullName: (
          <MDTypography>{display(item.approverFullName)}</MDTypography>
        ),
        approverAvatar: renderAvatar(
          item.approverAvatar,
          item.approverFullName
        ),
        notificationRecipients: (
          <MDTypography>
            {renderRecipients(item.notificationRecipients)}
          </MDTypography>
        ),
      })),
    })
  }

  const handlerAPIList = (params) => {
    trigger(params).then((res) => {
      handlerDataTable(res?.data?.data)
      setPagination({
        ...pagination,
        totalPage: res?.data?.data?.totalPage,
        currentPage: res?.data?.data?.currentPage,
      })
      setIsLoading(false)
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setState((prev) => ({
      ...prev,
      [name]: value,
    }))
  }
  const handleChangePage = (page) => {
    handlerAPIList({
      ...state,
      page: page,
    })
  }

  const updateDateFilter = (updates) => {
    setState((prev) => {
      const next = { ...prev, ...updates, page: 0 }
      if (next.startDate && next.endDate) {
        handlerAPIList(next)
      }
      return next
    })
  }

  // Fetch data when filters change
  useEffect(() => {
    const t = setTimeout(() => {
      handlerAPIList(state)
    }, 1000)
    return () => clearTimeout(t)
  }, [])

  return (
    <Grid
      item
      xs={12}
      lg={8}
      xxl={7.5}
      className={`teacher-management__section`}
    >
      {isLoading ? (
        <Loading style={{ height: 200, margin: "10% auto" }} />
      ) : (
        <MDBox className="teacher-management__table">
          <MDBox className="teacher-management__table-header">
            {/* <InfoIcon
              type={TooltipType.TYPE_1}
              content={`This is a list of teachers who have left the company.`}
              color={TooltipIconColor.BLUE}
            >
              <MDTypography variant="h3" className="info-icon__title">
                Leave
              </MDTypography>
            </InfoIcon> */}
            <MDTypography variant="h3" className="info-icon__title">
              Leave
            </MDTypography>
          </MDBox>

          <MDBox className="teacher-management__table-filters">
            <MDInput
              className="input input-search"
              placeholder="Search by name"
              name="keyword"
              value={state.keyword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="icon-search" />
                  </InputAdornment>
                ),
              }}
              onChange={handleInputChange}
            />
            <DateSelect
              onChangeStartDate={(e) => {
                updateDateFilter({
                  startDate: moment(e).format("YYYY-MM-DD"),
                  endDate: null,
                })
              }}
              onChangeEndDate={(e) => {
                if (!e) return
                updateDateFilter({
                  endDate: moment(e).format("YYYY-MM-DD"),
                })
              }}
              // disable={minimumFetchingTime}
              defaultStartDate={
                state.start ?? moment().startOf("isoweek").format("YYYY-MM-DD")
              }
              defaultEndDate={
                state.end ?? moment().endOf("isoweek").format("YYYY-MM-DD")
              }
            />
          </MDBox>
          <MDBox className="teacher-management__table-wrapper">
            <DataTable
              table={dataTable}
              entriesPerPage={{
                isShown: true,
                defaultValue: state.size,
                entries: [5, 10, 25],
                pagination: true,
              }}
            />
            <MDBox className="table-footer">
              <MDBox className="table-footer-showing">
                Showing {dataTable?.rows?.length} of {dataTable?.rows?.length}{" "}
                entries
              </MDBox>
              {pagination.totalPage >= 0 && (
                <ReactPaginate
                  className="pagination"
                  nextLabel={<Icon>chevron_right</Icon>}
                  previousLabel={<Icon>chevron_left</Icon>}
                  forcePage={Number(state.page)}
                  onPageChange={(event) => {
                    handleChangePage(event.selected)
                  }}
                  pageCount={pagination.totalPage}
                />
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      )}
    </Grid>
  )
}
